import clsx from 'clsx';

import { faComment } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useMemo, useState } from 'react';
import useCommentCard from '~/hooks/discussion/useCommentCard';
import { useUserStatus } from '~/hooks/userStatus';
import {
  DiscussionStatus,
  IDiscussionUserInfoProps,
  IQuestionCommentProps,
  IQuestionThreadProps,
  QuestionV2,
} from '~/lib/discussions/types';
import useUser from '~/lib/user';
import { CommentV2Type } from '~/static/discussions/enums';
import { HelpfulButton } from '../HelpfulButton';
import { CreatorReply, ICreatorReplyProps } from './CreatorReply';
import DiscussionOptions from './DiscussionOptions';
import DiscussionQuestionInfo from './DiscussionQuestionInfo';
import DiscussionTag from './DiscussionTag';
import QuestionThread from './QuestionThread';

function QuestionCard({
  question,
  partnersIds = [],
  mutateComment,
}: Readonly<{
  question: QuestionV2;
  partnersIds?: Array<number>;
  mutateComment?: () => void;
}>): JSX.Element {
  const [showThread, setShowThread] = useState(false);

  const {
    isPartner,
    stateHelpfulCount,
    stateHasUserUpvoted,
    toggleVote,
    disableVoteBtn,
    creatorReply,
    showDiscussionTag,
  } = useCommentCard(question, CommentV2Type.Question, partnersIds);

  const { user } = useUser();
  const { userStatus } = useUserStatus(question.deal_id);

  const mapToQuestionComment = useCallback(
    (reply: QuestionV2): IQuestionCommentProps => {
      const userInfo: IDiscussionUserInfoProps = {
        id: reply.user.id,
        username: reply.user.username,
        avatar: reply.user.avatar,
        isVerified: reply.purchased,
        isPlus: reply.user.has_plus ?? false,
        commentDate: new Date(reply.created),
        dealsBought: 0, // we don't show this in the question card,
        isFounder: partnersIds.includes(reply.user.id),
      };

      return {
        id: reply.id,
        dealId: question.deal_id,
        text: reply.comment,
        level: reply.level,
        userInfo,
        status: reply.status?.toLowerCase() as DiscussionStatus,
        comments: reply.children?.map((grandChild) =>
          mapToQuestionComment(grandChild),
        ),
      } as IQuestionCommentProps;
    },
    [partnersIds, question.deal_id],
  );

  const questionThread: IQuestionThreadProps = useMemo(() => {
    const nonCreatorReplies = (question?.children ?? []).filter(
      (child: QuestionV2) => child.id !== creatorReply?.id,
    );

    return {
      questionId: question.id,
      dealId: question.deal_id,
      comments: nonCreatorReplies.map((child) => mapToQuestionComment(child)),
    } as IQuestionThreadProps;
  }, [
    creatorReply?.id,
    mapToQuestionComment,
    question?.children,
    question.deal_id,
    question.id,
  ]);

  const creatorReplyAnswer: ICreatorReplyProps | null = useMemo(() => {
    return creatorReply
      ? { ...creatorReply, replyText: `A: ${creatorReply.replyText}` }
      : null;
  }, [creatorReply]);

  return (
    <div
      data-testid="questions-card-wrapper"
      className={clsx(
        'relative flex flex-col items-stretch gap-4 self-stretch rounded border border-gravel p-4 shadow-pdp',
        { 'pt-12': user?.id === question.user.id && !showDiscussionTag },
      )}
    >
      {/* Only owner can see options */}
      {user?.id === question.user.id && (
        <div className="absolute top-4 right-4 flex gap-3">
          <DiscussionOptions
            deleteUrl={`/api/v2/deals/${question.deal_id}/questions/${question.id}/`}
            discusstionType={CommentV2Type.Question}
            mutateComment={mutateComment}
          />
        </div>
      )}

      {showDiscussionTag && (
        <DiscussionTag
          isStaffOrPartner={userStatus?.user_moderator || isPartner}
          isOwner={user?.id === question.user.id}
          status={question.status?.toLowerCase() as DiscussionStatus}
          partnerHasReplied={!!creatorReply}
        />
      )}

      <DiscussionQuestionInfo
        comment={question.comment}
        userName={question.user.username}
        publishDate={new Date(question.created)}
        detailUrl={question.display_path}
      />

      {!!creatorReplyAnswer && (
        <CreatorReply {...creatorReplyAnswer} mutateComment={mutateComment} />
      )}

      <div className="flex w-full items-center justify-between">
        <button
          data-testid="question-comments-button"
          className="text-sm font-medium text-bolt hover:text-bolt-20"
          onClick={() => setShowThread(!showThread)}
          type="button"
        >
          <FontAwesomeIcon icon={faComment} className="mr-1" />
          Comment
          {!!questionThread.comments?.length && (
            <span>&nbsp;({questionThread.comments?.length})</span>
          )}
        </button>

        <HelpfulButton
          helpfulCount={stateHelpfulCount}
          hasUserUpvoted={stateHasUserUpvoted}
          isDisabled={disableVoteBtn}
          onToggleVote={toggleVote}
        />
      </div>

      {showThread && (
        <div className="w-full border-t border-sundog">
          <QuestionThread {...questionThread} />
        </div>
      )}
    </div>
  );
}

export default QuestionCard;
